// Shadow DOM mounting helper.
// Isolates the widget from host-page (WordPress theme/plugin) CSS by rendering
// into a shadow root and relocating the app's stylesheets inside that root.

export interface ShadowMountResult {
  // Element to render the React tree into (lives inside the shadow root).
  mountNode: HTMLElement;
  // The shadow root itself (use as portal container for overlays/dropdowns).
  shadowRoot: ShadowRoot;
}

// Per-shadow-root bookkeeping: which source <head> nodes we've already copied
// INTO this root. Keyed by shadow root so multiple widgets on one page each get
// their own full copy of the stylesheets (a page-global flag on the source node
// would let the first root "claim" a sheet and starve the others).
const copiedByRoot = new WeakMap<ShadowRoot, WeakSet<Element>>();

// CRA emits CSS as <style> (dev, via style-loader) and <link rel="stylesheet">
// (prod). We can't easily tell our CSS apart from the host's, so we copy every
// stylesheet in <head> into the shadow root. Host styles copied in this way are
// harmless because the host selectors won't match our hashed module classes;
// the point is to guarantee OUR styles are present inside the boundary.
function collectHeadStyleNodes(): Element[] {
  return Array.from(document.head.querySelectorAll('style, link[rel="stylesheet"]'));
}

function cloneIntoShadow(node: Element, shadowRoot: ShadowRoot): void {
  let copied = copiedByRoot.get(shadowRoot);
  if (!copied) {
    copied = new WeakSet<Element>();
    copiedByRoot.set(shadowRoot, copied);
  }

  // Skip only if THIS root already has a copy of THIS source node.
  if (copied.has(node)) return;
  copied.add(node);

  const clone = node.cloneNode(true) as Element;
  shadowRoot.appendChild(clone);
}

// Copies every current <head> stylesheet into the given shadow root.
function copyAllInto(shadowRoot: ShadowRoot): void {
  collectHeadStyleNodes().forEach((node) => cloneIntoShadow(node, shadowRoot));
}

// Watches <head> for stylesheets injected after mount: lazy-loaded route chunks
// and dev HMR both add <style>/<link> nodes late. Each tracked root gets its
// own copy of the new node.
function observeNewStyles(shadowRoot: ShadowRoot): void {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((added) => {
        if (!(added instanceof Element)) return;
        if (added.matches('style, link[rel="stylesheet"]')) {
          cloneIntoShadow(added, shadowRoot);
        }
      });
    }
  });

  observer.observe(document.head, { childList: true });
}

// Attaches a shadow root to `host` and returns a mount node inside it.
// The mount node carries both the data attribute and the FastCourierApp class
// so the scoped `app` mixin styles and `.FastCourierApp` portal containers keep
// working inside the shadow boundary.
export function createShadowMount(host: HTMLElement): ShadowMountResult {
  const shadowRoot = host.shadowRoot ?? host.attachShadow({ mode: 'open' });

  const mountNode = document.createElement('div');
  mountNode.className = 'FastCourierApp';
  // Carry the data attribute on the mount node itself. Inside a shadow root the
  // host element (which holds data-fast-courier-app) is outside the boundary, so
  // selectors like `[data-fast-courier-app] .FastCourierApp` can't match. Having
  // both markers on the mount node lets the scoped `app` mixin styles apply.
  mountNode.setAttribute('data-fast-courier-app', '');
  mountNode.setAttribute('data-fast-courier-shadow-mount', '');
  shadowRoot.appendChild(mountNode);

  // Copy stylesheets already present, then keep watching for new ones. A couple
  // of delayed re-syncs catch styles that style-loader injects just after the
  // component's JS first runs (common for lazily-evaluated screen modules).
  copyAllInto(shadowRoot);
  observeNewStyles(shadowRoot);
  window.setTimeout(() => copyAllInto(shadowRoot), 0);
  window.setTimeout(() => copyAllInto(shadowRoot), 500);

  return { mountNode, shadowRoot };
}

// Container resolver for react-bootstrap overlays (Modal, etc). Portals default
// to document.body, which is OUTSIDE the shadow root, so the overlay would lose
// our scoped styles. Point it at the shadow mount node when embedded, else fall
// back to the normal in-document .FastCourierApp root.
export function getOverlayContainer(): HTMLElement {
  const shadowRoot = (window as any).__fcShadowRoot as ShadowRoot | null;
  const inShadow = shadowRoot?.querySelector<HTMLElement>('.FastCourierApp');
  if (inShadow) return inShadow;

  const inDocument = document.querySelector<HTMLElement>('.FastCourierApp');
  return inDocument ?? document.body;
}

// --- Raise the shadow host's stacking context for modals --------------------
// A modal portaled into the shadow root is trapped in the host element's
// stacking context, so a WordPress sticky/fixed header can paint over it. While
// a modal is open we raise the HOST's stacking context (position:relative +
// high z-index) so its fixed modal/backdrop paint above the page chrome, then
// restore the original inline styles on close. The host box size is left alone
// so the inline widget behind the backdrop keeps its normal layout.

const HOST_SAVED_STYLE = 'data-fc-saved-csstext';

function getShadowHost(): HTMLElement | null {
  const shadowRoot = (window as any).__fcShadowRoot as ShadowRoot | null;
  return (shadowRoot?.host as HTMLElement) ?? null;
}

// Promotes the shadow host above page chrome without changing its box size.
// We only raise its stacking context (position:relative + high z-index) so the
// fixed modal/backdrop inside the shadow root paint above a sticky WP header,
// while the inline widget (search box) keeps its normal position and width.
export function enterOverlayLayer(): void {
  const host = getShadowHost();
  if (!host) return;
  if (host.hasAttribute(HOST_SAVED_STYLE)) return; // already promoted

  host.setAttribute(HOST_SAVED_STYLE, host.style.cssText);
  // position:relative establishes a stacking context so z-index takes effect;
  // size/flow are untouched, so the search box does not reflow.
  if (!host.style.position || host.style.position === 'static') {
    host.style.position = 'relative';
  }
  host.style.zIndex = '2147483000';
}

// Restores the host to its original inline styles when the modal closes.
export function exitOverlayLayer(): void {
  const host = getShadowHost();
  if (!host) return;
  if (!host.hasAttribute(HOST_SAVED_STYLE)) return;

  host.style.cssText = host.getAttribute(HOST_SAVED_STYLE) ?? '';
  host.removeAttribute(HOST_SAVED_STYLE);
}
